import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import ReactTooltip from 'react-tooltip';
import { VisuallyLogo } from '@/features/strapi/components/logo';
import { LogoUploadDropdown } from '@/components/logo-upload-dropdown';
import { nav } from '@/utils/browser';
import { AccountContext, useFeatureBit } from '@/features/account-context';
import { FeatureBit, StoreDetails } from '@/webapi/use-auth-api';
import { breakpoints } from '@/components/responsive';

const accountDomainTooltip = `account-domain-tooltip`;

interface LogoSectionProps {
  store?: StoreDetails;
}

export const LogoSection: FC<LogoSectionProps> = ({ store }) => {
  const hideBrandLogo = useFeatureBit(FeatureBit.HIDE_BRAND_LOGO);
  const { generalStoreSettings, setGeneralStoreSettings } =
    useContext(AccountContext);
  const [uploadedShopLogo, setUploadedShopLogo] = useState<string | undefined>(
    generalStoreSettings?.uploaded_shop_logo || undefined,
  );
  const [shopifyLogo, setShopifyLogo] = useState<string | undefined>(
    generalStoreSettings?.shop_logo || undefined,
  );

  const siteName = useMemo(
    () =>
      store?.domain
        ?.replace(`https://`, ``)
        ?.replace(`www.`, ``)
        ?.replace(`.myshopify.com`, ``),
    [store],
  );

  const storeDomain = useMemo(
    () => store?.domain?.replace(`https://`, ``)?.replace(`www.`, ``),
    [store],
  );

  const shouldShowSiteNameTooltip = useMemo(
    () => (storeDomain?.length || 0) > 20,
    [storeDomain],
  );

  useEffect(() => {
    if (!hideBrandLogo && generalStoreSettings) {
      setUploadedShopLogo(generalStoreSettings.uploaded_shop_logo);
      setShopifyLogo(generalStoreSettings.shop_logo);
    }
  }, [generalStoreSettings]);

  const onLogoUpdate = (logo: string | undefined) => {
    setUploadedShopLogo(logo);
    if (setGeneralStoreSettings && generalStoreSettings) {
      setGeneralStoreSettings({
        ...generalStoreSettings,
        uploaded_shop_logo: logo,
      });
    }
  };

  return (
    <Container>
      <VisuallyLogoButton onClick={() => nav(`/dashboard`)}>
        <VisuallyLogo small />
      </VisuallyLogoButton>
      {hideBrandLogo ? (
        <SiteName data-tip data-for={accountDomainTooltip}>
          {siteName || `Visually`}
        </SiteName>
      ) : (
        <LogoUploadDropdown
          storeAlias={store?.alias || ``}
          storeDomain={storeDomain || ``}
          shopifyLogo={shopifyLogo}
          uploadedShopLogo={uploadedShopLogo}
          currentSettings={generalStoreSettings}
          onLogoUpdated={onLogoUpdate}
        >
          {uploadedShopLogo ? (
            <BrandLogo
              src={uploadedShopLogo}
              alt={siteName || `Store logo`}
              data-tip
              data-for={accountDomainTooltip}
            />
          ) : (
            <SiteName data-tip data-for={accountDomainTooltip}>
              {siteName || `Visually`}
            </SiteName>
          )}
        </LogoUploadDropdown>
      )}
      {shouldShowSiteNameTooltip && (
        // @ts-ignore
        <ReactTooltip
          id={accountDomainTooltip}
          place="bottom"
          backgroundColor="#fff"
          textColor="#000"
          className="domain-tooltip"
        >
          {storeDomain}
        </ReactTooltip>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 1.5rem;
`;

const VisuallyLogoButton = styled.button`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const SiteName = styled.span`
  font-family: 'Inter', serif;
  font-size: 1.8rem;
  text-transform: uppercase;
  font-weight: bold;
  color: black;
  max-width: 24rem;
  letter-spacing: -0.79px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BrandLogo = styled.img`
  display: block;
  ${breakpoints.down(`md`)} {
    transform: scale(0.8);
  }
`;
